import LegalPageLayout, {
  type LegalSection,
  type TocItem,
} from "@/components/landing/shared/LegalPageLayout";
import CanonicalTags from "@/components/seo/CanonicalTags";

const TOC: TocItem[] = [
  { num: "01", title: "Introduction", id: "section-intro" },
  { num: "02", title: "Information We Collect", id: "section-collect" },
  { num: "03", title: "How We Use Your Information", id: "section-use" },
  { num: "04", title: "How We Share Your Information", id: "section-share" },
  { num: "05", title: "Your Privacy Rights", id: "section-rights" },
  { num: "06", title: "Data Retention", id: "section-retention" },
  { num: "07", title: "Data Deletion", id: "data-deletion" },
  { num: "08", title: "Anonymous Browsing", id: "section-anonymous" },
  { num: "09", title: "Security", id: "section-security" },
  { num: "10", title: "Children's Privacy", id: "section-children" },
  { num: "11", title: "Third-Party Links", id: "section-links" },
  { num: "12", title: "Changes to This Policy", id: "section-changes" },
  { num: "13", title: "Contact Us", id: "section-contact" },
];

const SECTIONS: LegalSection[] = [
  {
    num: "01",
    title: "Introduction",
    id: "section-intro",
    body: (
      <>
        <p>
          Compaire, Inc. ("Compaire," "we," "us," or "our") is committed to protecting the privacy
          of real estate professionals who use our platform. This Privacy Policy explains how we
          collect, use, disclose, and safeguard your information when you use the Compaire platform
          ("Platform").
        </p>
        <p>
          Anonymity is a core design principle of Compaire. Agents explore and compare teams
          without their identity being revealed to those organizations — ever — unless the agent
          explicitly chooses to initiate contact. This Policy explains the technical and policy
          measures we use to uphold that commitment.
        </p>
        <p>
          By using the Platform, you consent to the practices described in this Policy. If you do
          not agree with the terms of this Policy, please discontinue use of the Platform.
        </p>
      </>
    ),
  },
  {
    num: "02",
    title: "Information We Collect",
    id: "section-collect",
    body: (
      <>
        <span className="legal-subsec-eyebrow">2.1 Information You Provide</span>
        <p>When you create an account or use the Platform, you may provide:</p>
        <ul>
          <li>Name and email address (required for account creation)</li>
          <li>
            Professional details such as license number, years of experience, and current brokerage
          </li>
          <li>
            Career preferences including culture, leadership style, compensation expectations, and
            market focus
          </li>
          <li>
            Organizational profile information (for teams and brokerages), including compensation
            structure, culture description, and team size
          </li>
          <li>Communications you send to us or to other users through the Platform</li>
          <li>
            If you request to claim an organization: your name, role, work email address and phone
            number
          </li>
        </ul>

        <span className="legal-subsec-eyebrow">2.2 Information Collected Automatically</span>
        <p>
          When you use the Platform, we automatically collect certain technical data, including IP
          address, browser type and version, device identifiers, pages visited, time spent on
          pages, and referring URLs. This data is used for analytics, security, and improving the
          Platform experience.
        </p>

        <span className="legal-subsec-eyebrow">2.3 Cookies and Tracking</span>
        <p>
          We use cookies and similar tracking technologies to operate the Platform, remember your
          preferences, and analyze usage patterns. You may control cookie settings through your
          browser; however, disabling certain cookies may limit Platform functionality. For more
          detail, see our Cookie Policy.
        </p>

        <span className="legal-subsec-eyebrow">2.4 Information from Third Parties</span>
        <p>
          We may receive information about you from third-party services you connect to the
          Platform, such as calendar integrations or authentication providers. This information is
          used only to provide the features you requested and is not sold or shared beyond that
          purpose.
        </p>
      </>
    ),
    backToTop: true,
  },
  {
    num: "03",
    title: "How We Use Your Information",
    id: "section-use",
    body: (
      <>
        <p>We use the information we collect for the following purposes:</p>
        <ul>
          <li>To operate, maintain, and improve the Platform and its features</li>
          <li>To generate Fit Scores and personalized career intelligence recommendations</li>
          <li>To facilitate communication between agents and organizations when consent is given</li>
          <li>
            To send administrative communications, such as account confirmations and policy updates
          </li>
          <li>To detect, prevent, and address fraud, abuse, and security incidents</li>
          <li>To comply with applicable legal obligations</li>
        </ul>
        <p>
          We do not use your career intent data — including your comparison activity, saved
          organizations, or Fit Score interactions — for advertising purposes or to train external
          AI models. This data is used exclusively to deliver the Platform's core features to you.
        </p>
      </>
    ),
  },
  {
    num: "04",
    title: "How We Share Your Information",
    id: "section-share",
    body: (
      <>
        <span className="legal-subsec-eyebrow">
          4.1 With Organizations on the Platform (With Your Consent)
        </span>
        <p>
          Organizations listed on the Platform — teams, brokerages, and their representatives —
          never receive identifying information about you unless you explicitly initiate contact or
          grant permission. Aggregate, anonymized analytics (e.g., "your profile was viewed 47
          times this month") are shared with organizations as part of their subscription, but these
          views are not attributed to specific individuals.
        </p>

        <span className="legal-subsec-eyebrow">4.2 With Service Providers</span>
        <p>
          We share information with trusted third-party vendors who perform services on our behalf,
          such as cloud hosting, analytics processing, email delivery, and customer support
          tooling. These vendors are contractually required to use your information only to perform
          services for Compaire and may not use it for their own purposes.
        </p>

        <span className="legal-subsec-eyebrow">4.3 For Legal Reasons</span>
        <p>
          We may disclose your information if required to do so by law or in response to valid
          legal process, including subpoenas, court orders, or government requests. We will attempt
          to notify you of such requests where legally permitted and practically feasible.
        </p>

        <span className="legal-subsec-eyebrow">4.4 Business Transfers</span>
        <p>
          In the event of a merger, acquisition, or sale of all or a portion of Compaire's assets,
          your information may be transferred as part of the transaction. We will notify affected
          users prior to any such transfer and provide options where required by applicable law.
        </p>
      </>
    ),
  },
  {
    num: "05",
    title: "Your Privacy Rights and Choices",
    id: "section-rights",
    body: (
      <>
        <p>
          Depending on your location, you may have the following rights with respect to your
          personal information:
        </p>
        <div className="legal-def-list">
          <div className="legal-def-item">
            <span className="legal-def-term">Access</span>
            <span className="legal-def-desc">
              Request a copy of the personal information we hold about you.
            </span>
          </div>
          <div className="legal-def-item">
            <span className="legal-def-term">Correction</span>
            <span className="legal-def-desc">
              Request correction of inaccurate or incomplete information.
            </span>
          </div>
          <div className="legal-def-item">
            <span className="legal-def-term">Deletion</span>
            <span className="legal-def-desc">
              Request deletion of your personal information, subject to certain legal exceptions.
            </span>
          </div>
          <div className="legal-def-item">
            <span className="legal-def-term">Portability</span>
            <span className="legal-def-desc">
              Request a machine-readable copy of your data for transfer to another service.
            </span>
          </div>
          <div className="legal-def-item">
            <span className="legal-def-term">Objection</span>
            <span className="legal-def-desc">
              Object to certain processing activities, including profiling for marketing.
            </span>
          </div>
        </div>
        <div className="legal-callout">
          <div className="legal-callout-eyebrow">Key Right</div>
          <p>
            You may request deletion of your career intent data — including comparison history,
            saved organizations, and Fit Score interactions — at any time by emailing{" "}
            <a href="mailto:privacy@compaire.co">privacy@compaire.co</a>. Deletion requests are
            processed within 30 days. Your account and public-facing profile data are deleted
            separately upon account closure.
          </p>
        </div>
        <p>
          To exercise any of these rights, contact us at{" "}
          <a href="mailto:privacy@compaire.co">privacy@compaire.co</a>. We may require verification
          of your identity before processing your request.
        </p>
      </>
    ),
    backToTop: true,
  },
  {
    num: "06",
    title: "Data Retention",
    id: "section-retention",
    body: (
      <>
        <p>
          We retain your personal information for as long as your account is active or as needed to
          provide Platform services. Upon account closure, we delete your personal information
          within 30 days, except for the records described in section 7.4, which we keep for the
          reasons explained there.
        </p>
        <p>
          Career intent data — comparison activity, Fit Score history, and browsing behavior — is
          retained for no more than 24 months from the date of last activity, or until you request
          deletion, whichever comes first.
        </p>
      </>
    ),
  },
  {
    num: "07",
    title: "Data Deletion",
    id: "data-deletion",
    body: (
      <>
        <span className="legal-subsec-eyebrow">7.1 Requesting deletion of your data</span>
        <p>
          You have the right to request deletion of your personal information from Compaire at any
          time. This section explains what Compaire stores from a connected Meta account, how to
          disconnect it, and how to close your account and request deletion of your data.
        </p>

        <span className="legal-subsec-eyebrow">
          7.2 What Compaire collects from a connected Meta account
        </span>
        <p>
          When you sign in or connect your account using Facebook or Instagram (Meta), Compaire
          receives and stores:
        </p>
        <ul>
          <li>Your name and email address</li>
          <li>Your profile picture URL</li>
          <li>A Meta-issued user identifier used to authenticate your session</li>
        </ul>
        <p>
          Compaire does not receive or store your friends list, posts, messages, or any other
          activity from your Meta account.
        </p>

        <span className="legal-subsec-eyebrow">7.3 How to disconnect your Meta account</span>
        <p>To remove Compaire's access to your Meta account:</p>
        <ol
          style={{
            paddingLeft: 20,
            marginBottom: 16,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <li style={{ fontSize: 16, lineHeight: 1.6, color: "hsl(var(--ink-500))" }}>
            Open Facebook Settings &amp; Privacy → Settings
          </li>
          <li style={{ fontSize: 16, lineHeight: 1.6, color: "hsl(var(--ink-500))" }}>
            Select Apps and Websites
          </li>
          <li style={{ fontSize: 16, lineHeight: 1.6, color: "hsl(var(--ink-500))" }}>
            Find Compaire in your active apps and click Remove
          </li>
          <li style={{ fontSize: 16, lineHeight: 1.6, color: "hsl(var(--ink-500))" }}>
            Confirm the removal
          </li>
        </ol>
        <p>
          Removing Compaire from your Meta settings stops all future data sharing between Meta and
          Compaire. It does not automatically delete data already stored in your Compaire account.
        </p>

        <span className="legal-subsec-eyebrow">
          7.4 Closing your account and requesting deletion
        </span>
        <p>
          To close your Compaire account and delete your personal information, email{" "}
          <a href="mailto:data@compaire.co">data@compaire.co</a> from the email address on your
          account. We confirm receipt within 5 business days and complete closure within 30 days.
        </p>
        <p>
          <strong>What we delete.</strong> Your account and sign-in sessions, profile, career
          preferences, saved organizations, comparison history, Fit Scores, browsing and
          engagement history, follows, feed posts and comments, notifications, any request you made
          to claim an organization, files you uploaded, your waitlist sign-up, and feedback emails
          you sent us. We also remove you from our email mailing lists. We remove your name, email
          address, phone number, photo, bio and other personal details from any organization
          roster entry linked to your account, and your email address from invitations sent to the email address on your
          account; any such invitation still open is withdrawn. We remove the contact details and
          notes from your call bookings and cancel any booking that has not happened yet; we keep
          only the organization, date and time.
        </p>
        <p>
          <strong>What Compaire keeps, and why.</strong> We continue to hold the following records
          after closure. Removing your name or account link from a record does not necessarily
          make it anonymous, because text you wrote may still identify you.
        </p>
        <ul>
          <li>
            <strong>Messages:</strong> Messages you sent to a team or brokerage stay in that
            conversation, so the organization keeps its history. Your name and photo are removed;
            the message text stays.
          </li>
          <li>
            <strong>Reviews:</strong> Reviews you wrote stay published without your account
            attached, so the organization's review history stays intact. They no longer count
            toward star ratings.
          </li>
          <li>
            <strong>Roster entries:</strong> If an organization listed you on its team roster
            through your account, the entry stays as a past team member showing only your role and
            dates; your name, contact details, photo, bio and other personal details are removed.
          </li>
          <li>
            <strong>Roster entries an organization added itself:</strong> If an organization typed
            your details into its roster rather than linking your account, that entry is the
            organization's record and stays. To have it removed, email{" "}
            <a href="mailto:data@compaire.co">data@compaire.co</a>; we will review it and either
            remove it or tell you why we kept it.
          </li>
          <li>
            <strong>Organization admin activity:</strong> Actions you took as an organization
            admin, such as invitations you sent, decisions you made, and newsroom items or awards
            you published, stay with that organization.
          </li>
          <li>
            <strong>Security and audit records:</strong> Records of staff and account actions, some
            including an email address, are kept as long as needed to investigate misuse and
            resolve disputes.
          </li>
          <li>
            <strong>Provider records:</strong> Our email provider keeps a log of emails we sent
            you, and our error-monitoring provider keeps error reports tagged with your account ID,
            under their own retention periods.
          </li>
          <li>
            <strong>Backups:</strong> Deleted data can remain in our database backups for up to 7
            days until they expire. If we restore a backup, we repeat the deletion.
          </li>
        </ul>
        <p>
          <strong>Removing records we keep.</strong> If you want any of these removed, say so in
          your request or email <a href="mailto:data@compaire.co">data@compaire.co</a> later. We
          will review each record and either delete it or tell you which record we kept and why.
        </p>

        <span className="legal-subsec-eyebrow">7.5 Deletion status</span>
        <p>
          If you submitted a data deletion request through Meta and need to verify its status,
          email <a href="mailto:data@compaire.co">data@compaire.co</a> with your request reference
          number and we will confirm the status within 5 business days.
        </p>
      </>
    ),
  },
  {
    num: "08",
    title: "Anonymous Browsing",
    id: "section-anonymous",
    body: (
      <>
        <p>
          Agents browse organizations in anonymous mode by default. When you view, save, or compare
          a team or brokerage profile, the organization is not notified of your identity. They
          receive only aggregate engagement metrics (e.g., total profile views) with no individual
          attribution.
        </p>
        <p>
          You remain anonymous until you initiate direct contact with an organization through the
          Platform's messaging feature, at which point you are identified only to that specific
          organization and only for the purpose of that communication. You may withdraw contact at
          any time; however, the organization keeps messages already sent, including after you
          close your account (see section 7.4).
        </p>
      </>
    ),
  },
  {
    num: "09",
    title: "Security",
    id: "section-security",
    body: (
      <>
        <p>
          We implement industry-standard technical and organizational measures to protect your
          information against unauthorized access, disclosure, alteration, and destruction. These
          measures include encryption in transit and at rest, access controls, and regular
          security assessments.
        </p>
        <p>
          No method of transmission or storage is 100% secure. If you believe your account has been
          compromised, contact us immediately at{" "}
          <a href="mailto:security@compaire.co">security@compaire.co</a>.
        </p>
      </>
    ),
  },
  {
    num: "10",
    title: "Children's Privacy",
    id: "section-children",
    body: (
      <p>
        The Platform is not directed to individuals under the age of 18. We do not knowingly
        collect personal information from anyone under 18. If we become aware that we have
        collected information from a minor, we will delete it promptly. If you believe a minor has
        provided us with personal information, contact us at{" "}
        <a href="mailto:privacy@compaire.co">privacy@compaire.co</a>.
      </p>
    ),
  },
  {
    num: "11",
    title: "Third-Party Links",
    id: "section-links",
    body: (
      <p>
        The Platform may contain links to third-party websites or services. This Privacy Policy
        does not apply to those third parties. We encourage you to review the privacy policies of
        any third-party services you access through the Platform. Compaire is not responsible for
        the privacy practices or content of third-party services.
      </p>
    ),
  },
  {
    num: "12",
    title: "Changes to This Policy",
    id: "section-changes",
    body: (
      <>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices,
          technology, legal requirements, or other factors. We will notify you of material changes
          by updating the "Last Updated" date at the top of this page and, where the changes are
          significant, by sending a notification to your registered email address.
        </p>
        <p>
          Your continued use of the Platform after the effective date of any updated Privacy Policy
          constitutes your acceptance of the revised Policy.
        </p>
      </>
    ),
  },
  {
    num: "13",
    title: "Contact Us",
    id: "section-contact",
    body: (
      <>
        <p>
          If you have questions, concerns, or requests regarding this Privacy Policy or the
          handling of your personal information, please contact us:
        </p>
        <p>
          <strong>Compaire, Inc.</strong>
          <br />
          Privacy inquiries: <a href="mailto:privacy@compaire.co">privacy@compaire.co</a>
          <br />
          Account closure and deletion: <a href="mailto:data@compaire.co">data@compaire.co</a>
          <br />
          Security: <a href="mailto:security@compaire.co">security@compaire.co</a>
          <br />
          General: <a href="mailto:hello@compaire.co">hello@compaire.co</a>
        </p>
        <p>
          We will respond to all privacy inquiries within 10 business days. For deletion requests,
          processing may take up to 30 days.
        </p>
      </>
    ),
    backToTop: true,
  },
];

const PrivacyPage = () => {
  return (
    <>
      <CanonicalTags
        path="/privacy"
        title="Privacy Policy | Compaire"
        description="Read how Compaire collects, uses and shares personal information, and learn about your privacy rights, choices and data deletion options."
      />
      <LegalPageLayout
        eyebrow="Compaire, Inc."
        heading="Privacy Policy"
        effectiveLine="Effective April 17, 2026 · Last Updated October 26, 2026"
        tocItems={TOC}
        sections={SECTIONS}
      />
    </>
  );
};

export default PrivacyPage;
